import { Refresh } from "components/icons/Refresh";
import { useWindowSize } from "hooks/useWindowSize";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { VarianceComparisonReport } from "components/Reports/VarianceExplanation/VarianceComparisonReport";
import { ReportFilterType } from "components/Reports/ReportFilter";
import { VarianceReportDownload } from "components/Reports/VarianceExplanation/VarianceReportDownload";
import { VarianceReportSkeleton } from "components/Reports/VarianceExplanation/VarianceReportSkeleton";
import { INCOME_STATEMENT } from "constants/bookkeeping";
import { COMPARE_BY, VARIANCE } from "constants/reports";
import {
  VARIANCE_REPORT_MODE,
  VARIANCE_REPORT_STATUS,
  VarianceReportModeType,
} from "constants/financialClosing";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useGetFinancialClosingQuery } from "store/apis/financialClosing";
import ToolTip from "components/design/toolTip";

type VarianceReportPageProps = {
  listLink: string;
};

export const VarianceReportPage = ({ listLink }: VarianceReportPageProps) => {
  const { financialClosingId } = useParams<{ financialClosingId: string }>();
  const entityId = useCurrentEntityId();
  const history = useHistory();
  const [regenerateToken, setRegenerateToken] = useState(0);
  const { isMobile } = useWindowSize();

  const { data, isLoading, isSuccess } = useGetFinancialClosingQuery(
    { entityId, financialClosingId },
    { skip: !entityId || !financialClosingId }
  );

  const closing = data?.closing_card_details;

  const isGenerated =
    closing?.variance_report_status === VARIANCE_REPORT_STATUS.GENERATED;

  let mode: VarianceReportModeType = isGenerated
    ? VARIANCE_REPORT_MODE.VIEW
    : VARIANCE_REPORT_MODE.GENERATE;
  if (regenerateToken > 0) mode = VARIANCE_REPORT_MODE.REGENERATE;

  const close = () => history.push(listLink);

  const filter: ReportFilterType = useMemo(
    () => ({
      START_DATE: closing?.start_date || "",
      END_DATE: closing?.end_date || "",
      ACCOUNTING_METHOD: closing?.accounting_method!,
      HIDE_ZERO: true,
      COMPARE_BY: COMPARE_BY.PREVIOUS_PERIOD,
      VARIANCE: [VARIANCE.AMOUNT, VARIANCE.PERCENTAGE],
      VIEW_BY: null,
      TXN_GROUP_ID: null,
      CLASS_IDS: [],
      CLASSIFICATION: [],
      NO_CLASSIFICATION: [],
      OTHER: null,
      PERCENTAGE_MODE: null,
      FORECAST_ENABLED: false,
      FORECAST_START: "",
      FORECAST_END: "",
      FORECAST_SET_ID: null,
    }),
    [closing?.start_date, closing?.end_date, closing?.accounting_method]
  );

  return (
    <Modal.Root open modal={false} onOpenChange={close}>
      <Modal.Content
        size="fullscreen"
        onFocusOutside={(e) => e.preventDefault()}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <Modal.Header className="t-sticky t-top-0 t-z-sidebar t-gap-3 t-bg-white">
          <div className="t-flex t-w-full t-items-center t-justify-between">
            <Modal.Title>Flux report</Modal.Title>
            <div className="t-flex t-items-center t-gap-2">
              <ToolTip
                text={
                  <div className="t-w-52 t-text-start">
                    Refreshes this report with current financials. Any edited
                    explanations will be overridden
                  </div>
                }
              >
                <span>
                  <Button
                    customType={isMobile ? "icon" : "secondary"}
                    size="small"
                    aria-label="Regenerate report"
                    onClick={() => setRegenerateToken((token) => token + 1)}
                  >
                    {isMobile ? <Refresh size="16" /> : "Regenerate report"}
                  </Button>
                </span>
              </ToolTip>

              <VarianceReportDownload
                filter={filter}
                reportType={INCOME_STATEMENT}
              />
              <Modal.Close />
            </div>
          </div>
        </Modal.Header>
        <Modal.Body>
          <Async.Root
            isLoading={isLoading}
            isSuccess={isSuccess}
            isEmpty={false}
            customLoader={<VarianceReportSkeleton />}
          >
            <Async.Success>
              <VarianceComparisonReport
                reportType={INCOME_STATEMENT}
                filter={filter}
                mode={mode}
                regenerateToken={regenerateToken}
              />
            </Async.Success>
          </Async.Root>
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
};
